import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { getVisitorUUID } from '../utils/uuidManager';
import { validateAndFormatPhoneNumber, autoFormatPhoneInput, getPhoneValidationErrorMessage } from '../utils/phoneNumberValidator';
import Cookies from 'js-cookie';
import styled from 'styled-components';
import { prysmDB } from '../utils/database';
import { loggedSmsFetch } from '../utils/apiLogger';
import apiService from '../services/apiService';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || 'http://localhost:4000';
const COOKIE_NAME = process.env.REACT_APP_COOKIE_NAME || 'prysm-registration';

const Container = styled.div`
  max-width: 400px;
  margin: 0 auto;
  padding: 2rem;
  background: rgba(0, 0, 0, 0.8);
  border-radius: 12px;
  box-shadow: 0 4px 6px rgba(0, 0, 0, 0.1);
`;

const Title = styled.h2`
  color: #fff;
  text-align: center;
  margin-bottom: 2rem;
`;

const Form = styled.form`
  display: flex;
  flex-direction: column;
  gap: 1rem;
`;

const Input = styled.input`
  padding: 0.75rem;
  border: 1px solid #333;
  border-radius: 6px;
  background: rgba(255, 255, 255, 0.1);
  color: #fff;
  font-size: 1rem;
  
  &:focus {
    outline: none;
    border-color: #4a90e2;
  }
`;

const Button = styled.button`
  padding: 0.75rem;
  background: #4a90e2;
  color: white;
  border: none;
  border-radius: 6px;
  font-size: 1rem;
  cursor: pointer;
  transition: background 0.2s;
  
  &:hover {
    background: #357abd;
  }
  
  &:disabled {
    background: #666;
    cursor: not-allowed;
  }
`;

const ErrorMessage = styled.div`
  background-color: rgba(239, 68, 68, 0.1);
  border: 1px solid rgba(239, 68, 68, 0.2);
  color: #ef4444;
  padding: 0.75rem;
  border-radius: 0.5rem;
  margin-top: 1rem;
  display: flex;
  align-items: center;
  gap: 0.5rem;
  animation: fadeIn 0.3s ease-in-out;

  @keyframes fadeIn {
    from {
      opacity: 0;
      transform: translateY(-10px);
    }
    to {
      opacity: 1;
      transform: translateY(0);
    }
  }

  svg {
    flex-shrink: 0;
  }
`;

const SuccessMessage = styled.div`
  background-color: rgba(34, 197, 94, 0.1);
  border: 1px solid rgba(34, 197, 94, 0.2);
  color: #22c55e;
  padding: 0.75rem;
  border-radius: 0.5rem;
  margin-top: 1rem;
  display: flex;
  align-items: center;
  gap: 0.5rem;
  animation: fadeIn 0.3s ease-in-out;

  svg {
    flex-shrink: 0;
  }
`;

const ResendButton = styled.button`
  background: none;
  border: none;
  color: #4a90e2;
  cursor: pointer;
  font-size: 0.9rem;
  margin-top: 1rem;
  text-decoration: underline;
  
  &:hover {
    color: #357abd;
  }
  
  &:disabled {
    color: #666;
    cursor: not-allowed;
    text-decoration: none;
  }
`;

const SMSVerification = () => {
  const [verificationCode, setVerificationCode] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [phoneNumber, setPhoneNumber] = useState('');
  const [phoneValidation, setPhoneValidation] = useState({ isValid: false, errorMessage: '', suggestions: [] });
  const [codeSent, setCodeSent] = useState(false);
  const [countdown, setCountdown] = useState(0);
  const [showFormatTooltip, setShowFormatTooltip] = useState(false);
  const [isPhoneValid, setIsPhoneValid] = useState(true);
  const [step, setStep] = useState(1); // 1: Enter phone, 2: Enter code
  const navigate = useNavigate();
  const location = useLocation();
  const [success, setSuccess] = useState('');
  const [resendDisabled, setResendDisabled] = useState(false);
  const [resendTimer, setResendTimer] = useState(60);
  const [errorDetails, setErrorDetails] = useState(null); // For detailed error information
  const [showEmailFallback, setShowEmailFallback] = useState(false);
  const [emailFallbackConfirmed, setEmailFallbackConfirmed] = useState(false);

  useEffect(() => {
    const loadUserData = async () => {
      try {
        console.log('[SMSVerification] Using cookie name:', COOKIE_NAME);

        // Check if user is logged in
        const registrationData = await prysmDB.get('registration_data', 'current_registration');
        console.log('[SMSVerification] Session data for registration_data:', registrationData);

        if (!registrationData) {
          console.error('[SMSVerification] No user session found. Redirecting to welcome page...');
          navigate('/welcome');
          return;
        }

        // Get mobile number from registration data
        const mobileNumber = registrationData.mobile_number;
        if (!mobileNumber) {
          navigate('/register');
          return;
        }

        // Get phone number from registration data
        const storedPhoneNumber = registrationData.mobile_number;
        if (storedPhoneNumber) {
          setPhoneNumber(storedPhoneNumber);
          await prysmDB.set('phoneNumber', 'current_phone', storedPhoneNumber);

          // Validate the stored phone number
          const validation = validateAndFormatPhoneNumber(storedPhoneNumber);
          setPhoneValidation(validation);
          setIsPhoneValid(validation.isValid);
        }

        setPhoneNumber(mobileNumber);

        // Validate the mobile number from location state
        const validation = validateAndFormatPhoneNumber(mobileNumber);
        setPhoneValidation(validation);
        setIsPhoneValid(validation.isValid);
      } catch (error) {
        console.error('[SMSVerification] Error loading user data:', error);
        navigate('/welcome');
      }
    };

    loadUserData();
  }, [location, navigate]);

  useEffect(() => {
    let timer;
    if (countdown > 0) {
      timer = setInterval(() => {
        setCountdown(prev => prev - 1);
      }, 1000);
    }
    return () => clearInterval(timer);
  }, [countdown]);

  useEffect(() => {
    let timer;
    if (resendDisabled) {
      timer = setInterval(() => {
        setResendTimer((prev) => {
          if (prev <= 1) {
            setResendDisabled(false);
            return 0;
          }
          return prev - 1;
        });
      }, 1000);
    }
    return () => {
      if (timer) clearInterval(timer);
    };
  }, [resendDisabled]);

  // Comprehensive error handling function
  const handleError = (error, context = 'Unknown') => {
    console.error(`Error in ${context}:`, error);

    // Clear previous error details
    setErrorDetails(null);

    // Handle different types of errors
    if (error.name === 'TypeError' && error.message.includes('fetch')) {
      setError('Network Error: Unable to connect to the server. Please check your internet connection and try again.');
      setErrorDetails({
        type: 'NetworkError',
        message: error.message,
        context,
        stack: error.stack,
        suggestions: [
          'Check your internet connection',
          'Try refreshing the page',
          'Wait a few minutes and try again',
          'Contact support if the problem persists'
        ]
      });
    } else if (error.name === 'SyntaxError') {
      setError('Data Error: Received invalid response from server. Please try again.');
      setErrorDetails({
        type: 'DataError',
        message: error.message,
        context,
        stack: error.stack,
        suggestions: [
          'Try refreshing the page',
          'Clear your browser cache',
          'Try again in a few minutes',
          'Contact support if the problem continues'
        ]
      });
    } else if (error.message && error.message.includes('timeout')) {
      setError('Timeout Error: The request took too long to complete. Please try again.');
      setErrorDetails({
        type: 'TimeoutError',
        message: error.message,
        context,
        stack: error.stack,
        suggestions: [
          'Check your internet connection speed',
          'Try again in a few minutes',
          'Contact support if the problem persists'
        ]
      });
    } else if (error.message && error.message.includes('AbortError')) {
      setError('Request Cancelled: The operation was cancelled. Please try again.');
      setErrorDetails({
        type: 'AbortError',
        message: error.message,
        context,
        stack: error.stack,
        suggestions: [
          'Try the operation again',
          'Refresh the page if needed',
          'Contact support if the problem continues'
        ]
      });
    } else if (error.message && typeof error.message === 'string') {
      // Check for known server error patterns
      const knownErrors = {
        'INVALID_PHONE_FORMAT': 'Please enter a valid 10-digit US phone number',
        'CODE_ALREADY_SENT': 'A verification code has already been sent. Please wait before requesting a new one.',
        'CODE_MISMATCH': 'The verification code you entered is incorrect. Please try again.',
        'NO_CODE_FOUND': 'No verification code found. Please request a new code.',
        'TOO_MANY_ATTEMPTS': 'Too many attempts. Please wait a few minutes before trying again.',
        'CODE_EXPIRED': 'The verification code has expired. Please request a new one.',
        'PHONE_ALREADY_VERIFIED': 'This phone number has already been verified.',
        'SMS_SERVICE_ERROR': 'We\'re having trouble sending SMS messages right now. Please try again later.',
        'INVALID_CODE_LENGTH': 'Please enter a 6-digit verification code.',
        'INVALID_CODE_FORMAT': 'Verification code must contain only numbers.',
        'AUTHENTICATION_FAILED': 'Authentication failed. Please log in again.',
        'SESSION_EXPIRED': 'Your session has expired. Please log in again.',
        'INVALID_TOKEN': 'Invalid authentication token. Please log in again.',
        'RATE_LIMITED': 'Too many requests. Please wait a few minutes before trying again.',
        'SERVICE_UNAVAILABLE': 'Service temporarily unavailable. Please try again later.',
        'INTERNAL_SERVER_ERROR': 'Server error occurred. Please try again later.',
        'BAD_REQUEST': 'Invalid request. Please check your input and try again.',
        'FORBIDDEN': 'Access denied. Please contact support.',
        'NOT_FOUND': 'Requested resource not found. Please try again.',
        'CONFLICT': 'Conflict with existing data. Please contact support.'
      };

      // Check if it's a known error
      for (const [errorKey, errorMessage] of Object.entries(knownErrors)) {
        if (error.message.includes(errorKey) || error.message === errorMessage) {
          setError(errorMessage);
          return;
        }
      }

      // If it's a string but not a known error, show it with details
      setError(error.message);
      setErrorDetails({
        type: 'UnknownError',
        message: error.message,
        context,
        stack: error.stack,
        suggestions: [
          'Try the operation again',
          'Refresh the page',
          'Check your internet connection',
          'Contact support if the problem persists'
        ]
      });
    } else {
      // Completely unknown error - show with full details
      const errorMessage = error.message || 'An unexpected error occurred';
      setError(errorMessage);
      setErrorDetails({
        type: 'UnexpectedError',
        message: errorMessage,
        context,
        stack: error.stack,
        errorName: error.name,
        fullError: error.toString(),
        suggestions: [
          'Try refreshing the page',
          'Clear your browser cache',
          'Try again in a few minutes',
          'Contact support with the error details below'
        ]
      });
    }
  };

  const getErrorMessage = (errorCode, defaultMessage) => {
    const errorMessages = {
      'INVALID_PHONE_FORMAT': 'Please enter a valid 10-digit US phone number',
      'CODE_ALREADY_SENT': 'A verification code has already been sent. Please wait before requesting a new one.',
      'CODE_MISMATCH': 'The verification code you entered is incorrect. Please try again.',
      'NO_CODE_FOUND': 'No verification code found. Please request a new code.',
      'TOO_MANY_ATTEMPTS': 'Too many attempts. Please wait a few minutes before trying again.',
      'CODE_EXPIRED': 'The verification code has expired. Please request a new one.',
      'PHONE_ALREADY_VERIFIED': 'This phone number has already been verified.',
      'SMS_SERVICE_ERROR': 'We\'re having trouble sending SMS messages right now. Please try again later.',
      'INVALID_CODE_LENGTH': 'Please enter a 6-digit verification code.',
      'INVALID_CODE_FORMAT': 'Verification code must contain only numbers.',
      'AUTHENTICATION_FAILED': 'Authentication failed. Please log in again.',
      'SESSION_EXPIRED': 'Your session has expired. Please log in again.',
      'INVALID_TOKEN': 'Invalid authentication token. Please log in again.',
      'RATE_LIMITED': 'Too many requests. Please wait a few minutes before trying again.',
      'SERVICE_UNAVAILABLE': 'Service temporarily unavailable. Please try again later.',
      'INTERNAL_SERVER_ERROR': 'Server error occurred. Please try again later.',
      'BAD_REQUEST': 'Invalid request. Please check your input and try again.',
      'FORBIDDEN': 'Access denied. Please contact support.',
      'NOT_FOUND': 'Requested resource not found. Please try again.',
      'CONFLICT': 'Conflict with existing data. Please contact support.'
    };

    return errorMessages[errorCode] || defaultMessage || 'An unexpected error occurred. Please try again.';
  };

  const validatePhoneNumber = (number) => {
    // Basic E.164 format validation
    const phoneRegex = /^\+[1-9]\d{1,14}$/;
    return phoneRegex.test(number);
  };

  const handlePhoneChange = (e) => {
    const input = e.target.value;

    // Auto-format the input as user types
    const formatted = autoFormatPhoneInput(input);
    setPhoneNumber(formatted);
    setError('');

    // Validate the phone number
    const validation = validateAndFormatPhoneNumber(input);
    setPhoneValidation(validation);
    setIsPhoneValid(validation.isValid);
  };

  const handleSendCode = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError('');
    setSuccess('');
    setErrorDetails(null);

    try {
      console.log('Starting send code process...');

      // Use comprehensive phone validation
      const validation = validateAndFormatPhoneNumber(phoneNumber);
      console.log('Phone validation result:', validation);

      if (!validation.isValid) {
        console.log('Invalid phone number format:', validation.errorMessage);
        const errorMsg = getPhoneValidationErrorMessage(validation);
        setError(errorMsg);
        setLoading(false);
        return;
      }

      const formattedNumber = validation.formattedNumber;
      console.log('Formatted phone number for AWS SNS:', formattedNumber);

      // Validate authentication
      let token = window.getAuthToken ? window.getAuthToken() : Cookies.get(COOKIE_NAME);
      const userData = await prysmDB.get('registration_data', 'current_registration');
      console.log('[SMSVerification] Auth check - Token:', token ? 'Present' : 'Missing', 'UserData:', userData ? 'Present' : 'Missing');

      if (!token) {
        // Try to reset session and get new token
        if (window.resetSession) {
          await window.resetSession();
          token = window.getAuthToken ? window.getAuthToken() : Cookies.get(COOKIE_NAME);
          if (!token) {
            console.log('Missing authentication - unable to create new session');
            setError('Authentication Error: Unable to create new session');
            setErrorDetails({
              type: 'AuthenticationError',
              message: 'Authentication token missing and unable to create new session',
              context: 'handleSendCode',
              suggestions: [
                'Try refreshing the page',
                'Clear your browser cache',
                'Try again in a few minutes',
                'Contact support if the problem continues'
              ]
            });
            setLoading(false);
            return;
          }
        } else {
          console.log('Missing authentication');
          setError('Authentication Error: Please log in to continue');
          setErrorDetails({
            type: 'AuthenticationError',
            message: 'Missing authentication token',
            context: 'handleSendCode',
            suggestions: [
              'Please log in again',
              'Refresh the page and try again',
              'Clear your browser cache if problems persist'
            ]
          });
          setLoading(false);
          return;
        }
      }

      if (!userData) {
        console.log('Missing user data');
        setError('User Data Error: Please complete registration first');
        setErrorDetails({
          type: 'UserDataError',
          message: 'User registration data missing',
          context: 'handleSendCode',
          suggestions: [
            'Complete the registration form first',
            'Try refreshing the page',
            'Contact support if the problem persists'
          ]
        });
        setLoading(false);
        return;
      }

      // User data is already an object from storage
      const parsedUserData = userData;

      const userId = await getVisitorUUID();
      console.log('Extracted user ID:', userId);

      if (!userId) {
        console.log('No user ID found in session data');
        setError('Session Error: User session is invalid. Please log in again.');
        setErrorDetails({
          type: 'SessionError',
          message: 'No user ID found in session data',
          context: 'handleSendCode',
          suggestions: [
            'Please log in again',
            'Refresh the page and try again',
            'Clear your browser cache if problems persist'
          ]
        });
        setLoading(false);
        return;
      }

      // Validate request data
      if (!formattedNumber || !userId) {
        throw new Error('Missing required data for SMS request');
      }

      console.log('Sending SMS code request');

      const response = await apiService.sendSMSCode({
        userId,
        phoneNumber: formattedNumber
      });

      console.log('Response status:', response.status);

      // Handle different response types
      let data;
      try {
        data = await response.json();
        console.log('Response data:', data);
      } catch (jsonError) {
        throw new Error(`Invalid response format from server: ${jsonError.message}`);
      }

      if (!response.ok) {
        const errorMessage = getErrorMessage(data.error, data.message);
        throw new Error(errorMessage);
      }

      // Store SMS result in storage safely
      try {
        if (data.smsresult) {
          await prysmDB.set('sms_send_result', 'current_sms', data.smsresult);
          console.log('[SMSVerification] Stored SMS result in storage:', data.smsresult);
        }
      } catch (storageError) {
        console.warn('Failed to store SMS result in session storage:', storageError);
        // Don't fail the entire operation for this
      }

      setSuccess('Verification code sent successfully! Check your phone for the code.');
      setCodeSent(true);
      startResendTimer();
    } catch (err) {
      handleError(err, 'handleSendCode');
    } finally {
      setLoading(false);
    }
  };

  const handleVerifyCode = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError('');
    setSuccess('');
    setErrorDetails(null);

    try {
      // Validate verification code format
      if (!verificationCode || verificationCode.trim() === '') {
        setError('Please enter a verification code');
        setErrorDetails({
          type: 'ValidationError',
          message: 'Verification code is required',
          context: 'handleVerifyCode',
          suggestions: [
            'Enter the 6-digit code sent to your phone',
            'Check your SMS messages',
            'Request a new code if needed'
          ]
        });
        setLoading(false);
        return;
      }

      if (!/^\d{6}$/.test(verificationCode)) {
        setError('Please enter a valid 6-digit verification code');
        setErrorDetails({
          type: 'ValidationError',
          message: 'Verification code must be exactly 6 digits',
          context: 'handleVerifyCode',
          inputValue: verificationCode,
          suggestions: [
            'Enter exactly 6 digits',
            'Remove any spaces or special characters',
            'Check that you entered the code correctly'
          ]
        });
        setLoading(false);
        return;
      }

      // Validate authentication
      let token = window.getAuthToken ? window.getAuthToken() : Cookies.get(COOKIE_NAME);
      const userData = await prysmDB.get('registration_data', 'current_registration');

      if (!token) {
        // Try to reset session and get new token
        if (window.resetSession) {
          await window.resetSession();
          token = window.getAuthToken ? window.getAuthToken() : Cookies.get(COOKIE_NAME);
          if (!token) {
            setError('Authentication Error: Unable to create new session');
            setErrorDetails({
              type: 'AuthenticationError',
              message: 'Authentication token missing and unable to create new session',
              context: 'handleVerifyCode',
              suggestions: [
                'Try refreshing the page',
                'Clear your browser cache',
                'Try again in a few minutes',
                'Contact support if the problem continues'
              ]
            });
            setLoading(false);
            return;
          }
        } else {
          setError('Authentication Error: Please log in to continue');
          setErrorDetails({
            type: 'AuthenticationError',
            message: 'Missing authentication token',
            context: 'handleVerifyCode',
            suggestions: [
              'Please log in again',
              'Refresh the page and try again',
              'Clear your browser cache if problems persist'
            ]
          });
          setLoading(false);
          return;
        }
      }

      if (!userData) {
        setError('User Data Error: Please complete registration first');
        setErrorDetails({
          type: 'UserDataError',
          message: 'User registration data missing',
          context: 'handleVerifyCode',
          suggestions: [
            'Complete the registration form first',
            'Try refreshing the page',
            'Contact support if the problem persists'
          ]
        });
        setLoading(false);
        return;
      }

      // User data is already an object from storage
      const parsedUserData = userData;

      const userId = await getVisitorUUID();

      if (!userId) {
        setError('Session Error: User session is invalid. Please log in again.');
        setErrorDetails({
          type: 'SessionError',
          message: 'No user ID found in session data',
          context: 'handleVerifyCode',
          suggestions: [
            'Please log in again',
            'Refresh the page and try again',
            'Clear your browser cache if problems persist'
          ]
        });
        setLoading(false);
        return;
      }

      // Validate phone number
      const phoneValidationResult = phoneValidation.formattedNumber || validateAndFormatPhoneNumber(phoneNumber).formattedNumber;
      if (!phoneValidationResult) {
        setError('Phone Number Error: Unable to format phone number for verification');
        setErrorDetails({
          type: 'PhoneValidationError',
          message: 'Phone number validation failed',
          context: 'handleVerifyCode',
          phoneNumber: phoneNumber,
          suggestions: [
            'Try entering your phone number again',
            'Refresh the page and start over',
            'Contact support if the problem persists'
          ]
        });
        setLoading(false);
        return;
      }

      // Validate request data
      if (!userId || !phoneValidationResult || !verificationCode) {
        throw new Error('Missing required data for verification request');
      }

      console.log('Sending SMS verification request');

      const response = await apiService.verifySMSCode({
        userId,
        phoneNumber: phoneValidationResult,
        code: verificationCode
      });

      console.log('Response status:', response.status);

      // Handle different response types
      let data;
      try {
        data = await response.json();
        console.log('Response data:', data);
      } catch (jsonError) {
        throw new Error(`Invalid response format from server: ${jsonError.message}`);
      }

      if (!response.ok) {
        const errorMessage = getErrorMessage(data.error, data.message);
        throw new Error(errorMessage);
      }

      setSuccess('Phone number verified successfully! Redirecting to checkout...');

      // Navigate with error handling
      try {
        setTimeout(() => {
          navigate('/checkout');
        }, 2000);
      } catch (navError) {
        console.error('Navigation error:', navError);
        setSuccess('Phone number verified successfully! Please proceed to checkout manually.');
      }
    } catch (err) {
      handleError(err, 'handleVerifyCode');
    } finally {
      setLoading(false);
    }
  };

  const formatPhoneNumber = (input) => {
    // Remove all non-digit characters
    const digits = input.replace(/\D/g, '');

    // Handle 10-digit numbers (add +1)
    if (digits.length === 10) {
      return `+1${digits}`;
    }

    // Handle 11-digit numbers starting with 1 (add +)
    if (digits.length === 11 && digits.startsWith('1')) {
      return `+${digits}`;
    }

    return input;
  };

  const handlePhoneNumberChange = (e) => {
    const input = e.target.value;

    // Auto-format the input as user types
    const formatted = autoFormatPhoneInput(input);
    setPhoneNumber(formatted);
    setError('');

    // Validate the phone number
    const validation = validateAndFormatPhoneNumber(input);
    setPhoneValidation(validation);
    setIsPhoneValid(validation.isValid);
  };

  const startResendTimer = () => {
    setResendDisabled(true);
    setResendTimer(60);
  };

  const handleEmailFallback = async () => {
    try {
      // Get user data from storage
      const userData = await prysmDB.get('registration_data', 'current_registration');
      if (!userData) {
        setError('Session Error: Unable to access user data for email fallback');
        return;
      }

      // Update session data to indicate email verification is required
      const updatedUserData = {
        ...userData,
        phone_verification_required: true,
        phone_verification_method: 'email_fallback',
        phone_verification_note: 'Email verification will be sent on first login attempt',
        phone_verification_timestamp: new Date().toISOString(),
        phone_number: phoneNumber
      };

      // Save updated data back to storage
      await prysmDB.set('registration_data', 'current_registration', updatedUserData);

      // Dispatch event to notify other components of data update
      window.dispatchEvent(new CustomEvent('registrationDataUpdated', { detail: updatedUserData }));

      // Mark email fallback as confirmed
      setEmailFallbackConfirmed(true);
      setShowEmailFallback(false);
      setSuccess('Email verification preference saved. You will receive an email verification code when you attempt to log in for the first time.');

      // Navigate to checkout after a short delay
      setTimeout(() => {
        navigate('/checkout');
      }, 3000);

    } catch (error) {
      handleError(error, 'handleEmailFallback');
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-b from-gray-900 to-gray-800 relative overflow-hidden">
      {/* Animated background elements */}
      <div className="absolute inset-0 overflow-hidden">
        <div className="absolute -top-1/2 -left-1/2 w-full h-full bg-gradient-to-r from-purple-500/20 to-pink-500/20 rounded-full blur-3xl animate-blob"></div>
        <div className="absolute -top-1/2 -right-1/2 w-full h-full bg-gradient-to-r from-blue-500/20 to-purple-500/20 rounded-full blur-3xl animate-blob animation-delay-2000"></div>
        <div className="absolute -bottom-1/2 -left-1/2 w-full h-full bg-gradient-to-r from-pink-500/20 to-blue-500/20 rounded-full blur-3xl animate-blob animation-delay-4000"></div>
      </div>

      <div className="relative z-10 min-h-screen flex items-center justify-center p-4">
        <div className="w-full max-w-md">
          <div className="bg-gray-800/80 backdrop-blur-lg rounded-2xl shadow-xl p-8 transform transition-all duration-300 hover:scale-[1.02] hover:shadow-2xl">
            <div className="text-center mb-8">
              <h1 className="text-4xl font-bold text-white mb-4 animate-fade-in">
                {step === 1 ? 'Verify Your Phone' : '🔐 Enter Verification Code'}
              </h1>
              <div className="relative inline-block">
                <div className="h-1 w-full bg-gradient-to-r from-purple-500 to-pink-500 rounded-full absolute -bottom-2 animate-glow"></div>
              </div>
            </div>

            {!codeSent ? (
              <Form onSubmit={handleSendCode}>
                <div className="relative">
                  <div className="flex items-center justify-between mb-2">
                    <label htmlFor="phoneNumber" className="block text-sm font-medium text-gray-300">
                      Phone Number
                    </label>
                    <button
                      type="button"
                      onMouseEnter={() => setShowFormatTooltip(true)}
                      onMouseLeave={() => setShowFormatTooltip(false)}
                      className="text-gray-400 hover:text-gray-300 focus:outline-none"
                    >
                      <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                      </svg>
                    </button>
                  </div>

                  {showFormatTooltip && (
                    <div className="absolute right-0 top-0 mt-8 w-64 bg-gray-700 rounded-lg shadow-lg p-3 text-sm text-gray-300 z-10">
                      <p className="mb-2">Enter your US phone number:</p>
                      <ul className="list-disc list-inside space-y-1">
                        <li>Include area code (first 3 digits)</li>
                        <li>Include your 7-digit phone number</li>
                        <li>No need to add +1 or country code</li>
                        <li>Examples: (555) 123-4567, 555-123-4567, 555.123.4567, or 5551234567</li>
                      </ul>
                    </div>
                  )}

                  <div className="relative">
                    <input
                      type="tel"
                      id="phoneNumber"
                      value={phoneNumber}
                      readOnly
                      className="w-full px-4 py-3 bg-gray-700 text-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-purple-500 cursor-not-allowed"
                    />
                  </div>

                  {!isPhoneValid && phoneNumber && phoneValidation.errorMessage && (
                    <div className="mt-2 p-3 bg-red-900/20 border border-red-700 rounded-lg">
                      <p className="text-sm text-red-400 font-medium mb-2">
                        {phoneValidation.errorMessage}
                      </p>
                      {phoneValidation.suggestions.length > 0 && (
                        <ul className="text-xs text-red-300 space-y-1">
                          {phoneValidation.suggestions.map((suggestion, index) => (
                            <li key={index} className="flex items-start">
                              <span className="text-red-400 mr-2">•</span>
                              <span>{suggestion}</span>
                            </li>
                          ))}
                        </ul>
                      )}
                    </div>
                  )}
                </div>

                <Button type="submit" disabled={loading || !phoneNumber || !isPhoneValid}>
                  {loading ? (
                    <div className="flex items-center justify-center">
                      <div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin mr-2"></div>
                      Sending...
                    </div>
                  ) : (
                    'Send Verification Code'
                  )}
                </Button>

                {error && (
                  <ErrorMessage>
                    <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                    </svg>
                    <span>{error}</span>
                  </ErrorMessage>
                )}

                {/* Detailed Error Information */}
                {errorDetails && (
                  <div className="mt-4 p-4 bg-red-900/20 border border-red-700 rounded-lg">
                    <div className="flex items-center mb-3">
                      <svg className="w-5 h-5 mr-2 text-red-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                      </svg>
                      <h4 className="text-sm font-medium text-red-300">Technical Details</h4>
                    </div>

                    <div className="space-y-2 text-xs text-red-200">
                      <div>
                        <span className="font-medium">Error Type:</span> {errorDetails.type}
                      </div>
                      <div>
                        <span className="font-medium">Context:</span> {errorDetails.context}
                      </div>
                      <div>
                        <span className="font-medium">Message:</span> {errorDetails.message}
                      </div>

                      {errorDetails.inputValue && (
                        <div>
                          <span className="font-medium">Input Value:</span> {errorDetails.inputValue}
                        </div>
                      )}

                      {errorDetails.phoneNumber && (
                        <div>
                          <span className="font-medium">Phone Number:</span> {errorDetails.phoneNumber}
                        </div>
                      )}

                      {errorDetails.errorName && (
                        <div>
                          <span className="font-medium">Error Name:</span> {errorDetails.errorName}
                        </div>
                      )}

                      {errorDetails.stack && (
                        <details className="mt-3">
                          <summary className="font-medium cursor-pointer hover:text-red-100">
                            Stack Trace (Click to expand)
                          </summary>
                          <pre className="mt-2 p-2 bg-red-900/30 rounded text-xs overflow-x-auto whitespace-pre-wrap">
                            {errorDetails.stack}
                          </pre>
                        </details>
                      )}

                      {errorDetails.fullError && (
                        <details className="mt-3">
                          <summary className="font-medium cursor-pointer hover:text-red-100">
                            Full Error Details (Click to expand)
                          </summary>
                          <pre className="mt-2 p-2 bg-red-900/30 rounded text-xs overflow-x-auto whitespace-pre-wrap">
                            {errorDetails.fullError}
                          </pre>
                        </details>
                      )}
                    </div>

                    {errorDetails.suggestions && errorDetails.suggestions.length > 0 && (
                      <div className="mt-3 pt-3 border-t border-red-700">
                        <h5 className="text-xs font-medium text-red-300 mb-2">Suggestions:</h5>
                        <ul className="text-xs text-red-200 space-y-1">
                          {errorDetails.suggestions.map((suggestion, index) => (
                            <li key={index} className="flex items-start">
                              <span className="text-red-400 mr-2">•</span>
                              <span>{suggestion}</span>
                            </li>
                          ))}
                        </ul>
                      </div>
                    )}
                  </div>
                )}

                {success && (
                  <SuccessMessage>
                    <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                    </svg>
                    <span>{success}</span>
                  </SuccessMessage>
                )}

                {/* Email Fallback Option */}
                {!emailFallbackConfirmed && !codeSent && (
                  <div className="mt-4 p-4 bg-blue-900/20 border border-blue-700 rounded-lg">
                    <div className="flex items-center mb-3">
                      <svg className="w-5 h-5 mr-2 text-blue-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M3 8l7.89 4.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
                      </svg>
                      <h4 className="text-sm font-medium text-blue-300">Having trouble receiving SMS?</h4>
                    </div>

                    <p className="text-xs text-blue-200 mb-3">
                      Can't receive text messages? You can verify your phone number via email instead.
                    </p>

                    <button
                      type="button"
                      onClick={handleEmailFallback}
                      className="w-full px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white text-sm rounded-lg transition-colors duration-200"
                    >
                      Verify via Email Instead
                    </button>

                    <div className="mt-2 text-xs text-blue-300">
                      <p>• You'll receive an email verification code on your first login attempt</p>
                      <p>• Your phone number will still be verified by other means for account security</p>
                      <p>• You can change this preference later in your account settings</p>
                    </div>
                  </div>
                )}

                {/* Email Fallback Confirmation */}
                {emailFallbackConfirmed && (
                  <div className="mt-4 p-4 bg-green-900/20 border border-green-700 rounded-lg">
                    <div className="flex items-center mb-3">
                      <svg className="w-5 h-5 mr-2 text-green-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                      </svg>
                      <h4 className="text-sm font-medium text-green-300">Email Verification Scheduled</h4>
                    </div>

                    <div className="text-xs text-green-200 space-y-2">
                      <p>✅ Email verification preference saved to your account</p>
                      <p>📧 You'll receive a verification code via email on your first login attempt</p>
                      <p>⏱️ Redirecting to checkout in a moment...</p>
                    </div>
                  </div>
                )}
              </Form>
            ) : (
              <Form onSubmit={handleVerifyCode}>
                <div>
                  <label htmlFor="verificationCode" className="block text-sm font-medium text-gray-300 mb-2">
                    Verification Code
                  </label>
                  <input
                    type="text"
                    id="verificationCode"
                    value={verificationCode}
                    onChange={(e) => setVerificationCode(e.target.value)}
                    className="w-full px-4 py-3 bg-gray-700/50 border border-gray-600 rounded-lg text-white placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-purple-500 focus:border-transparent transition-all duration-200"
                    placeholder="Enter 6-digit code"
                    maxLength={6}
                    required
                  />
                </div>

                <Button type="submit" disabled={loading || !verificationCode}>
                  {loading ? (
                    <div className="flex items-center justify-center">
                      <div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin mr-2"></div>
                      Verifying...
                    </div>
                  ) : (
                    'Verify Code'
                  )}
                </Button>

                {error && (
                  <ErrorMessage>
                    <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                    </svg>
                    <span>{error}</span>
                  </ErrorMessage>
                )}

                {/* Detailed Error Information */}
                {errorDetails && (
                  <div className="mt-4 p-4 bg-red-900/20 border border-red-700 rounded-lg">
                    <div className="flex items-center mb-3">
                      <svg className="w-5 h-5 mr-2 text-red-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                      </svg>
                      <h4 className="text-sm font-medium text-red-300">Technical Details</h4>
                    </div>

                    <div className="space-y-2 text-xs text-red-200">
                      <div>
                        <span className="font-medium">Error Type:</span> {errorDetails.type}
                      </div>
                      <div>
                        <span className="font-medium">Context:</span> {errorDetails.context}
                      </div>
                      <div>
                        <span className="font-medium">Message:</span> {errorDetails.message}
                      </div>

                      {errorDetails.inputValue && (
                        <div>
                          <span className="font-medium">Input Value:</span> {errorDetails.inputValue}
                        </div>
                      )}

                      {errorDetails.phoneNumber && (
                        <div>
                          <span className="font-medium">Phone Number:</span> {errorDetails.phoneNumber}
                        </div>
                      )}

                      {errorDetails.errorName && (
                        <div>
                          <span className="font-medium">Error Name:</span> {errorDetails.errorName}
                        </div>
                      )}

                      {errorDetails.stack && (
                        <details className="mt-3">
                          <summary className="font-medium cursor-pointer hover:text-red-100">
                            Stack Trace (Click to expand)
                          </summary>
                          <pre className="mt-2 p-2 bg-red-900/30 rounded text-xs overflow-x-auto whitespace-pre-wrap">
                            {errorDetails.stack}
                          </pre>
                        </details>
                      )}

                      {errorDetails.fullError && (
                        <details className="mt-3">
                          <summary className="font-medium cursor-pointer hover:text-red-100">
                            Full Error Details (Click to expand)
                          </summary>
                          <pre className="mt-2 p-2 bg-red-900/30 rounded text-xs overflow-x-auto whitespace-pre-wrap">
                            {errorDetails.fullError}
                          </pre>
                        </details>
                      )}
                    </div>

                    {errorDetails.suggestions && errorDetails.suggestions.length > 0 && (
                      <div className="mt-3 pt-3 border-t border-red-700">
                        <h5 className="text-xs font-medium text-red-300 mb-2">Suggestions:</h5>
                        <ul className="text-xs text-red-200 space-y-1">
                          {errorDetails.suggestions.map((suggestion, index) => (
                            <li key={index} className="flex items-start">
                              <span className="text-red-400 mr-2">•</span>
                              <span>{suggestion}</span>
                            </li>
                          ))}
                        </ul>
                      </div>
                    )}
                  </div>
                )}

                {success && (
                  <SuccessMessage>
                    <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                    </svg>
                    <span>{success}</span>
                  </SuccessMessage>
                )}

                <ResendButton
                  onClick={handleSendCode}
                  disabled={resendDisabled || loading}
                >
                  {resendDisabled
                    ? `Resend code in ${resendTimer}s`
                    : 'Resend verification code'}
                </ResendButton>

                {/* Email Fallback Option for Verification Step */}
                {!emailFallbackConfirmed && (
                  <div className="mt-4 p-4 bg-blue-900/20 border border-blue-700 rounded-lg">
                    <div className="flex items-center mb-3">
                      <svg className="w-5 h-5 mr-2 text-blue-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M3 8l7.89 4.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
                      </svg>
                      <h4 className="text-sm font-medium text-blue-300">Still having trouble?</h4>
                    </div>

                    <p className="text-xs text-blue-200 mb-3">
                      Can't receive the SMS code? Switch to email verification instead.
                    </p>

                    <button
                      type="button"
                      onClick={handleEmailFallback}
                      className="w-full px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white text-sm rounded-lg transition-colors duration-200"
                    >
                      Switch to Email Verification
                    </button>

                    <div className="mt-2 text-xs text-blue-300">
                      <p>• You'll receive an email verification code on your first login attempt</p>
                      <p>• This will save your phone number for future verification</p>
                    </div>
                  </div>
                )}
              </Form>
            )}

            <div className="mt-8 text-sm text-gray-400">
              <p className="mb-4">
                By verifying your phone number, you agree to receive text messages from OurPrysm. You can control these messages in your account settings anytime.
              </p>
              <p className="mb-4">
                We keep your phone number private and only use it for OurPrysm services. We won't share it with other companies unless you give us permission.
              </p>
              <p className="mb-4">
                Didn't request this verification? Just ignore the message or contact our support team if you have questions.
              </p>
              <p className="text-xs text-gray-500 border-t border-gray-700 pt-3 mt-4">
                * Standard text message rates may apply. Check with your phone carrier for details.
              </p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default SMSVerification;
